import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * The one class component in this codebase.
 *
 * The repo's rule is functional components only, and this is the single
 * documented exception: React exposes error boundaries *exclusively* through
 * `getDerivedStateFromError` and `componentDidCatch`, which are class-only
 * lifecycle methods. There is no hook, and there is no functional equivalent
 * — the alternative is a dependency wrapping this same class.
 *
 * It is kept to the minimum that requires a class: catch, remember, report,
 * and offer a reset. Everything the user actually sees is a normal function
 * component passed in as `fallback`.
 */

export interface ErrorBoundaryProps {
  children: ReactNode;
  /** Rendered instead of the children once something has thrown. */
  fallback: (error: Error, reset: () => void) => ReactNode;
  /**
   * Somewhere to send the error besides the console — a reporter, when the
   * cloud track brings one. Called with the component stack, which is the part
   * that actually says where the failure was.
   */
  onError?: (error: Error, info: ErrorInfo) => void;
  /**
   * Change this to clear the error automatically.
   *
   * Passing the document id means opening a different document gets a clean
   * attempt rather than the previous one's failure, without the user having to
   * dismiss anything.
   */
  resetKey?: string | number;
}

interface ErrorBoundaryState {
  /**
   * Whether something was thrown — kept separate from the value.
   *
   * `throw undefined`, `throw null` and `throw ''` are all legal, and testing
   * the value alone would read them as "nothing is wrong": the boundary would
   * render the children again, they would throw again, and React eventually
   * unmounts the root — the blank page this component exists to prevent.
   */
  caught: boolean;
  error: Error | null;
  resetKey: string | number | undefined;
}

/**
 * Anything can be thrown; only an `Error` carries a message and a stack.
 *
 * A thrown string is kept as the message rather than flattened to "unknown",
 * because in an app with no server-side log it may be the only clue there is.
 */
function toError(thrown: unknown): Error {
  if (thrown instanceof Error) return thrown;
  if (typeof thrown === 'string' && thrown.length > 0) return new Error(thrown);
  return new Error(`Something threw a non-error value (${String(thrown)})`);
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = {
    caught: false,
    error: null,
    resetKey: undefined,
  };

  static getDerivedStateFromError(thrown: unknown): Partial<ErrorBoundaryState> {
    return { caught: true, error: toError(thrown) };
  }

  static getDerivedStateFromProps(
    props: ErrorBoundaryProps,
    state: ErrorBoundaryState,
  ): Partial<ErrorBoundaryState> | null {
    if (state.resetKey === props.resetKey) return null;
    // A new subject to render: forget the previous one's failure.
    return { caught: false, error: null, resetKey: props.resetKey };
  }

  override componentDidCatch(thrown: unknown, info: ErrorInfo): void {
    const error = toError(thrown);
    // The console keeps the whole thing, including the component stack, which
    // is what makes an opaque crash diagnosable. The user gets the fallback.
    console.error('[burfy-docs] unhandled error', error, info.componentStack);
    this.props.onError?.(error, info);
  }

  readonly #reset = (): void => {
    this.setState({ caught: false, error: null });
  };

  override render(): ReactNode {
    const { caught, error } = this.state;
    if (caught) return this.props.fallback(error ?? toError(undefined), this.#reset);
    return this.props.children;
  }
}
